import { resolveMeterColor, useTranslation } from "@next-platform/client-common-core";
import formatCombinedRuleCondition from "@next-platform/client-common-core/rulebook/profile/formatCombinedRuleCondition";
import { Chip, ChipVariant, IconSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { CombinedRule, MeterDefinition, RuleCondition } from "@next-platform/packages-ts-types";
import { memo } from "react";

import { Col } from "@/components";
import RulebookMeterIcon from "@/components/Rulebook/RulebookMeterIcon";
import RulebookTokenText from "@/components/Rulebook/RulebookTokenText";
import Trans from "@/components/Trans/Trans";
import { useMeterColorTheme } from "@/hooks";

import RulebookProfileSectionHeading from "./RulebookProfileSectionHeading";
import useProfileTokenRenderer from "./useProfileTokenRenderer";

type RulebookCombinedRulesSectionProps = {
  rules: CombinedRule[];
  meters: MeterDefinition[];
};

const CombinedRuleConditionChip = ({
  condition,
  meters,
  translate,
}: {
  condition: RuleCondition;
  meters: MeterDefinition[];
  translate: (key: string, values?: Record<string, unknown>) => string;
}) => {
  const theme = useMeterColorTheme();
  const meter = meters.find((candidate) => candidate.id === condition.meter_id);
  const iconColor = meter ? resolveMeterColor({ color: meter.color, theme }) : undefined;

  return (
    <Chip variant={ChipVariant.SOFT_NEUTRAL}>
      {meter ? <RulebookMeterIcon icon={meter.icon} meterType={meter.type} color={iconColor} size={IconSize.XSMALL} /> : null}
      {formatCombinedRuleCondition({ condition, meters, translate })}
    </Chip>
  );
};

/** "When meters combine" — condition chips, caption and instruction per combined rule. */
const RulebookCombinedRulesSection = ({ rules, meters }: RulebookCombinedRulesSectionProps) => {
  const { t } = useTranslation();
  const renderChip = useProfileTokenRenderer({ definitionMeters: meters });

  if (!rules.length) {
    return null;
  }

  return (
    <Col className="gap-3" dataTestId="RulebookProfile-Combined">
      <RulebookProfileSectionHeading
        title={<Trans i18nKey="rulebook:profile.combined.title" />}
        subtitle={<Trans i18nKey="rulebook:profile.combined.subtitle" />}
      />
      <Col className="gap-2">
        {rules.map((rule) => (
          <div
            key={rule.id}
            className="flex flex-col gap-2 rounded-[14px] border border-solid border-gray-5 dark:border-gray-8 bg-gray-2 p-4"
            data-testid={`RulebookProfile-Combined-${rule.id}`}
          >
            <div className="flex flex-wrap gap-1">
              {rule.conditions.map((condition, index) => (
                <CombinedRuleConditionChip key={index} condition={condition} meters={meters} translate={t} />
              ))}
            </div>
            {rule.caption ? (
              <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                “<RulebookTokenText text={rule.caption} renderChip={renderChip} />”
              </Text>
            ) : null}
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
              <RulebookTokenText text={rule.instruction} renderChip={renderChip} />
            </Text>
          </div>
        ))}
      </Col>
    </Col>
  );
};

export default memo(RulebookCombinedRulesSection);
